// qs-usePerformanceMonitor.ts
import { ref, type Ref } from 'vue'

interface PerformanceMemory {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

interface PerformanceReport {
  fps: number
  frameTime: number
  memoryUsage: number
  componentRenderTime: number
  composableLoadTime: number
  warnings: string[]
  timestamp: string
}

interface PerformanceThresholds {
  fpsLow: number
  fpsHigh: number
  frameTime: number
  memory: number
  componentRender: number
  composableLoad: number
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory
}

let globalInstance: ReturnType<typeof createPerformanceMonitor> | null = null

function createPerformanceMonitor() {
  const fps: Ref<number> = ref(60)
  const frameTime: Ref<number> = ref(16.67)
  const memoryUsage: Ref<number> = ref(0)
  const componentRenderTime: Ref<number> = ref(0)
  const composableLoadTime: Ref<number> = ref(0)

  const frameCount: Ref<number> = ref(0)
  const lastTime: Ref<number> = ref(0)
  const renderStartTime: Ref<number> = ref(0)
  const composableStartTime: Ref<number> = ref(0)

  const warnings: Ref<string[]> = ref([])
  const isMonitoring: Ref<boolean> = ref(false)

  const thresholds: PerformanceThresholds = {
    fpsLow: 30,
    fpsHigh: 55,
    frameTime: 33.33,
    memory: 100 * 1024 * 1024,
    componentRender: 16,
    composableLoad: 10
  }

  let animationFrameId: number | null = null

  const hasMemoryAPI = (): boolean => {
    try {
      const perf = performance as PerformanceWithMemory
      return (
        'memory' in perf &&
        typeof perf.memory === 'object' &&
        perf.memory !== null &&
        typeof perf.memory.usedJSHeapSize === 'number' &&
        perf.memory.usedJSHeapSize > 0
      )
    } catch {
      return false
    }
  }

  const getMemoryUsage = (): number => {
    try {
      if (hasMemoryAPI()) {
        const memory = (performance as PerformanceWithMemory).memory as PerformanceMemory
        const used = memory.usedJSHeapSize
        return typeof used === 'number' && used > 0 ? used : 0
      }
    } catch (error) {
      console.warn('Failed to get memory usage:', error)
    }
    return 0
  }

  const formatMemorySize = (bytes: number): string => {
    return (bytes / 1024 / 1024).toFixed(2)
  }

  const checkPerformanceIssues = (): void => {
    const newWarnings: string[] = []

    try {
      if (fps.value < thresholds.fpsLow) {
        newWarnings.push(`Low FPS: ${fps.value} (threshold: ${thresholds.fpsLow})`)
      }

      if (frameTime.value > thresholds.frameTime) {
        newWarnings.push(`High frame time: ${frameTime.value.toFixed(2)}ms (threshold: ${thresholds.frameTime}ms)`)
      }

      if (memoryUsage.value > thresholds.memory) {
        const memoryMB = formatMemorySize(memoryUsage.value)
        const thresholdMB = formatMemorySize(thresholds.memory)
        newWarnings.push(`High memory usage: ${memoryMB}MB (threshold: ${thresholdMB}MB)`)
      }

      warnings.value = newWarnings
    } catch (error) {
      console.error('Error checking performance issues:', error)
    }
  }

  const monitor = (): void => {
    if (!isMonitoring.value) {
      animationFrameId = null
      return
    }

    try {
      const now = performance.now()
      frameCount.value++

      if (lastTime.value === 0) {
        lastTime.value = now
      }

      if (now - lastTime.value >= 1000) {
        const elapsed = now - lastTime.value
        if (elapsed > 0 && frameCount.value > 0) {
          fps.value = Math.round((frameCount.value * 1000) / elapsed)
          frameTime.value = elapsed / frameCount.value
        }

        memoryUsage.value = getMemoryUsage()
        checkPerformanceIssues()

        frameCount.value = 0
        lastTime.value = now
      }

      if (isMonitoring.value) {
        animationFrameId = requestAnimationFrame(monitor)
      } else {
        animationFrameId = null
      }
    } catch (error) {
      console.error('Error in performance monitoring loop:', error)
      stopMonitoring()
    }
  }

  const startMonitoring = (): void => {
    if (isMonitoring.value) {
      console.warn('Performance monitoring is already running')
      return
    }

    try {
      lastTime.value = performance.now()
      composableStartTime.value = performance.now()
      isMonitoring.value = true

      memoryUsage.value = getMemoryUsage()
      animationFrameId = requestAnimationFrame(monitor)
    } catch (error) {
      console.error('Failed to start performance monitoring:', error)
    }
  }

  const stopMonitoring = (): void => {
    try {
      isMonitoring.value = false
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
      frameCount.value = 0
    } catch (error) {
      console.error('Failed to stop performance monitoring:', error)
    }
  }

  const startRenderMeasurement = (): void => {
    try {
      renderStartTime.value = performance.now()
    } catch (error) {
      console.error('Failed to start render measurement:', error)
    }
  }

  const endRenderMeasurement = (): void => {
    try {
      if (renderStartTime.value > 0) {
        componentRenderTime.value = performance.now() - renderStartTime.value
        renderStartTime.value = 0
      }
    } catch (error) {
      console.error('Failed to end render measurement:', error)
    }
  }

  const startComposableMeasurement = (): void => {
    try {
      composableStartTime.value = performance.now()
    } catch (error) {
      console.error('Failed to start composable measurement:', error)
    }
  }

  const endComposableMeasurement = (): void => {
    try {
      if (composableStartTime.value > 0) {
        composableLoadTime.value = performance.now() - composableStartTime.value
        composableStartTime.value = 0
      }
    } catch (error) {
      console.error('Failed to end composable measurement:', error)
    }
  }

  const getPerformanceReport = (): PerformanceReport => {
    return {
      fps: fps.value,
      frameTime: frameTime.value,
      memoryUsage: memoryUsage.value,
      componentRenderTime: componentRenderTime.value,
      composableLoadTime: composableLoadTime.value,
      warnings: [...warnings.value],
      timestamp: new Date().toISOString()
    }
  }

  const clearWarnings = (): void => {
    warnings.value = []
  }

  const getOptimizationSuggestions = (): string[] => {
    const suggestions: string[] = []

    try {
      if (fps.value < thresholds.fpsLow) {
        suggestions.push('Consider reducing canvas complexity or enabling adaptive throttling')
      }

      if (frameTime.value > thresholds.frameTime) {
        suggestions.push('Consider using requestAnimationFrame batching for better performance')
      }

      if (memoryUsage.value > thresholds.memory) {
        suggestions.push('Consider clearing caches or reducing brush cache size')
      }

      if (componentRenderTime.value > thresholds.componentRender) {
        suggestions.push('Consider splitting large components into smaller ones')
      }

      if (composableLoadTime.value > thresholds.composableLoad) {
        suggestions.push('Consider lazy loading composables or optimizing imports')
      }
    } catch (error) {
      console.error('Error generating optimization suggestions:', error)
    }

    return suggestions
  }

  const exportPerformanceData = (): void => {
    try {
      const data = getPerformanceReport()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.download = `performance-report-${Date.now()}.json`
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export performance data:', error)
    }
  }

  const resetMetrics = (): void => {
    try {
      fps.value = 60
      frameTime.value = 16.67
      memoryUsage.value = 0
      componentRenderTime.value = 0
      composableLoadTime.value = 0
      frameCount.value = 0
      lastTime.value = 0
      renderStartTime.value = 0
      composableStartTime.value = 0
      warnings.value = []
    } catch (error) {
      console.error('Failed to reset metrics:', error)
    }
  }

  const startMonitoringManually = (): void => {
    startMonitoring()
  }

  return {
    fps,
    frameTime,
    memoryUsage,
    componentRenderTime,
    composableLoadTime,
    warnings,
    isMonitoring,
    startRenderMeasurement,
    endRenderMeasurement,
    startComposableMeasurement,
    endComposableMeasurement,
    getPerformanceReport,
    clearWarnings,
    getOptimizationSuggestions,
    exportPerformanceData,
    startMonitoringManually,
    stopMonitoring,
    resetMetrics
  }
}

export function usePerformanceMonitor() {
  if (!globalInstance) {
    globalInstance = createPerformanceMonitor()
  }
  return globalInstance
}